import { useEffect, useState } from 'react';
import { ONBOARDING_LOGOS } from '../../assets/brand-logos';
import ellipseOuter from '../../assets/svg/ellipse-outer.svg';
import ellipseInner from '../../assets/svg/ellipse-inner.svg';
import receiptLabel from '../../assets/svg/receipt-label.svg';
import payoutBadgeIcon from '../../assets/payout-badge-icon.webp';
import { ProgressIndicators } from '../progress-indicators';
import { OnboardingContinueButton } from '../onboarding-continue-button';
import { OnboardingScreen, ScaleToFit } from '../onboarding-layout';

const START_AMOUNT = 635.64;
const GROW_PER_TICK = 0.03;
const TICK_MS = 40;
const LOGO_COUNT = ONBOARDING_LOGOS.length;
const LOGO_STEP_DEG = 360 / LOGO_COUNT;

interface Step1Props {
  onContinue: () => void;
}

function formatMoneyParts(value: number): { dollars: string; cents: string } {
  const [dollars, cents] = value.toFixed(2).split('.');
  return {
    dollars: Number(dollars).toLocaleString('en-US'),
    cents,
  };
}

export function OnboardingStep1({ onContinue }: Step1Props) {
  const [amount, setAmount] = useState(START_AMOUNT);
  const { dollars, cents } = formatMoneyParts(amount);

  useEffect(() => {
    const id = window.setInterval(() => {
      setAmount((prev) => prev + GROW_PER_TICK);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <OnboardingScreen
      slot="onboarding-step-1"
      header={
        <div className="flex w-full flex-col items-center gap-3">
          <ProgressIndicators currentStep={0} />

          <div className="w-full text-center">
            <h1 className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
              Someone <span className="text-mf-green">owes you money</span> right now
            </h1>
            <p className="mt-2 text-base leading-5 text-mf-neutral-600">
              We track down every forgotten charge and unclaimed payout, then put real money back in
              your pocket.
            </p>
          </div>
        </div>
      }
      footer={<OnboardingContinueButton onClick={onContinue} />}
    >
      <ScaleToFit naturalHeight={320} lockWidth>
        <div className="relative h-[320px] w-full overflow-hidden">
        <img
          src={ellipseOuter}
          alt=""
          className="pointer-events-none absolute left-1/2 top-6 size-[1150px] max-w-none -translate-x-1/2"
        />
        <img
          src={ellipseInner}
          alt=""
          className="pointer-events-none absolute left-1/2 top-[196px] size-[822px] max-w-none -translate-x-1/2"
        />

        <div className="absolute left-1/2 top-[15px] z-20 flex -translate-x-1/2 items-center gap-2 overflow-hidden rounded-full bg-white py-2 pl-3 pr-4 shadow-[0_4px_8px_rgba(26,22,19,0.05)]">
          <img
            src={payoutBadgeIcon}
            alt=""
            className="size-5 shrink-0 object-cover"
          />
          <span className="whitespace-nowrap text-[15px] font-bold leading-5 text-mf-neutral-900">
            Don&apos;t miss your payouts
          </span>
        </div>

        {/* Centered like Figma's (frameWidth - 1080) / 2 — works at any container width */}
        <div className="absolute left-1/2 top-[60px] size-[1080px] -translate-x-1/2">
          <div className="relative size-full animate-[onboarding-logo-spin_48s_linear_infinite]">
            {ONBOARDING_LOGOS.map((src, index) => (
              <div
                key={src}
                className="absolute inset-0"
                style={{ transform: `rotate(${index * LOGO_STEP_DEG}deg)` }}
              >
                <img
                  src={src}
                  alt=""
                  className="absolute left-1/2 top-0 size-[104px] -translate-x-1/2 rounded-[28px] object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="absolute left-1/2 top-[196px] z-10 w-[192px] -translate-x-1/2">
          <div className="relative h-24 w-full">
            <img
              src={receiptLabel}
              alt=""
              className="absolute inset-0 size-full max-w-none"
            />
            <div className="absolute inset-x-0 top-5 flex flex-col items-center gap-1 text-center">
              <p className="w-full text-sm font-bold leading-4 text-mf-lime">UNCLAIMED MONEY</p>
              <p className="w-full font-display text-[28px] font-bold leading-9 text-white">
                <span className="tabular-nums">${dollars}</span>
                <span>.</span>
                <span className="tabular-nums">{cents}</span>
              </p>
            </div>
          </div>
        </div>
        </div>
      </ScaleToFit>
    </OnboardingScreen>
  );
}
